import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { createLead } from '@/services/leads'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Send } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { FormConfig } from '@/services/content'

// Dynamic schema builder based on config
const createFormSchema = (config?: FormConfig) => {
  const showEmployeeCount = config?.show_employee_count ?? true
  const showUsesExcel = config?.show_uses_excel ?? true

  return z.object({
    full_name: z.string().min(2, 'O nome deve ter pelo menos 2 caracteres'),
    email: z.string().email('Email inválido'),
    company: z.string().min(2, 'O nome da empresa é obrigatório'),
    employee_count: showEmployeeCount
      ? z.string({ required_error: 'Selecione o nº de colaboradores' })
      : z.string().optional(),
    business_area: z.string(),
    main_challenge: z.string().optional(),
    uses_excel: showUsesExcel
      ? z.enum(['yes', 'no'], {
          required_error: 'Por favor indique se usa Excel',
        })
      : z.enum(['yes', 'no']).optional(),
  })
}

type SectorLeadFormProps = {
  sectorTitle: string
  config?: FormConfig
}

export const SectorLeadForm = ({
  sectorTitle,
  config,
}: SectorLeadFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()

  const formSchema = createFormSchema(config)
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      full_name: '',
      email: '',
      company: '',
      business_area: sectorTitle,
      main_challenge: '',
    },
  })

  const showEmployeeCount = config?.show_employee_count ?? true
  const showUsesExcel = config?.show_uses_excel ?? true
  const challengeLabel =
    config?.custom_challenge_label || 'Qual o seu principal desafio atual?'

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true)
    try {
      const { error } = await createLead({
        full_name: values.full_name,
        email: values.email,
        company: values.company,
        business_area: values.business_area,
        employee_count: values.employee_count,
        uses_excel: values.uses_excel === 'yes',
        message: values.main_challenge || 'Submetido via página do setor',
      })

      if (error) throw error

      toast({
        title: 'Pedido recebido!',
        description: 'Entraremos em contacto brevemente com a sua proposta.',
      })
      form.reset()
    } catch (error) {
      console.error('Error submitting form:', error)
      toast({
        variant: 'destructive',
        title: 'Erro ao enviar',
        description:
          'Tente novamente ou envie email para geral@nucleodigital.app',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="w-full max-w-2xl mx-auto shadow-xl border-t-4 border-t-primary">
      <CardHeader className="text-center pb-2">
        <CardTitle className="text-2xl font-bold text-slate-900">
          Veja como isto se aplica ao seu negócio
        </CardTitle>
        <p className="text-slate-600">
          Receba uma proposta personalizada para a sua realidade.
        </p>
      </CardHeader>
      <CardContent className="p-6 md:p-8">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="full_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome Completo</FormLabel>
                    <FormControl>
                      <Input placeholder="João Silva" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Profissional</FormLabel>
                    <FormControl>
                      <Input placeholder="joao@empresa.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Empresa</FormLabel>
                    <FormControl>
                      <Input placeholder="Nome da Empresa Lda" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {showEmployeeCount && (
                <FormField
                  control={form.control}
                  name="employee_count"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nº de Colaboradores</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="1-10">1 - 10</SelectItem>
                          <SelectItem value="11-50">11 - 50</SelectItem>
                          <SelectItem value="51-200">51 - 200</SelectItem>
                          <SelectItem value="201+">Mais de 200</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <FormField
              control={form.control}
              name="main_challenge"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{challengeLabel}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ex: Controlo de stock, Gestão de leads..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {showUsesExcel && (
              <FormField
                control={form.control}
                name="uses_excel"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel>Utiliza Excel para gerir operações?</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="flex space-x-6"
                      >
                        <FormItem className="flex items-center space-x-2 space-y-0">
                          <FormControl>
                            <RadioGroupItem value="yes" />
                          </FormControl>
                          <FormLabel className="font-normal cursor-pointer">
                            Sim, usamos bastante
                          </FormLabel>
                        </FormItem>
                        <FormItem className="flex items-center space-x-2 space-y-0">
                          <FormControl>
                            <RadioGroupItem value="no" />
                          </FormControl>
                          <FormLabel className="font-normal cursor-pointer">
                            Não / Pouco
                          </FormLabel>
                        </FormItem>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <Button
              type="submit"
              size="lg"
              className="w-full text-lg font-semibold h-12 shadow-md hover:shadow-lg transition-all"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />A enviar...
                </>
              ) : (
                <>
                  Quero a minha proposta personalizada
                  <Send className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>
            <p className="text-xs text-center text-slate-400 mt-4">
              Os seus dados são confidenciais e usados apenas para esta análise.
            </p>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
